import React, { useEffect, useState } from 'react';
import api from '../api';
import { DataGrid } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import RefreshIcon from '@mui/icons-material/Refresh';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const role = (typeof window !== 'undefined' && localStorage.getItem('adminRole')) || '';
const isSuperAdmin = role && role.toUpperCase() === 'SUPER ADMIN';
const columns = [
    { field: 'id', headerName: 'ID', flex: 0.5, minWidth: 70 },
    { field: 'firstName', headerName: 'First Name', flex: 1, minWidth: 120 },
    { field: 'lastName', headerName: 'Last Name', flex: 1, minWidth: 120 },
    { field: 'email', headerName: 'Email', flex: 1.5, minWidth: 180 },
    { field: 'role', headerName: 'Role', flex: 0.8, minWidth: 120 },
    { field: 'schoolName', headerName: 'School', flex: 1.5, minWidth: 180 },
    { field: 'otpVerified', headerName: 'OTP Verified', flex: 0.7, minWidth: 120, type: 'boolean' },
    { field: 'createdAt', headerName: 'Created At', flex: 1, minWidth: 160 },
    {
        field: 'action',
        headerName: 'Actions',
        flex: 1,
        minWidth: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
            <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => params.row.onDelete(params.row)}
                disabled={!isSuperAdmin}
                sx={!isSuperAdmin ? { backgroundColor: '#e0e0e0', color: '#888', border: 'none' } : {}}
            >
                Delete
            </Button>
        ),
    },
];

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/api/users');
            const data = res.data;
            // normalize role into `role` property for display (handle various backend shapes)
            setUsers(data.map(u => ({
                ...u,
                role: u.role || u.roleName || (u.roles && u.roles.length ? (u.roles[0].name || u.roles[0].role) : '') || '',
                onDelete: handleDeleteClick
            })));
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to load users.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (user) => {
        setSelectedUser(user);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedUser) return;
        try {
            setLoading(true);
            await api.delete(`/admin/api/users/${selectedUser.id}`);
            setSnackbar({ open: true, message: 'User deleted successfully.', severity: 'success' });
            loadUsers();
        } catch (err) {
            const errMsg = err.response?.data ?? err.message;
            setSnackbar({ open: true, message: typeof errMsg === 'string' ? errMsg : 'Failed to delete user.', severity: 'error' });
        } finally {
            setLoading(false);
            setDeleteDialogOpen(false);
            setSelectedUser(null);
        }
    };

    const handleDeleteDialogClose = () => {
        setDeleteDialogOpen(false);
        setSelectedUser(null);
    };

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)', width: '90%', margin: '40px auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <Typography variant="h4" component="h2">
                    User Management
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={loadUsers}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>
            <Box sx={{ flex: 1, width: '100%' }}>
                <DataGrid
                    rows={users}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    loading={loading}
                    getRowId={(row) => row.id}
                    disableColumnMenu
                    sx={{
                        height: '100%',
                        '& .MuiDataGrid-columnHeaderTitle': { display: 'block', whiteSpace: 'normal', lineHeight: 1.2, fontWeight: 700, textDecoration: 'underline' },
                        '& .MuiDataGrid-columnHeader .MuiChip-root, & .MuiDataGrid-columnHeader .MuiChip-label': { fontWeight: 400, textDecoration: 'none' },
                    }}
                />
            </Box>
            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
                <DialogTitle>Delete User</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete user "{selectedUser?.firstName} {selectedUser?.lastName}" (ID: {selectedUser?.id})? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteDialogClose}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default UserManagement;
